import React, { FC, Fragment, ReactNode, useEffect } from 'react';
import { FetchMoreOptions } from 'apollo-client';

import { PaginatedUserFeedFollowsInput, UserFeedFollow, useGetFollowedQuery } from 'api';

export interface IGetFollowed {
  followed: UserFeedFollow[];
  count: number;
}

interface IProps {
  children: (props: IGetFollowed) => ReactNode;
  variables: PaginatedUserFeedFollowsInput;
}

const GetFollowed: FC<IProps> = ({ variables, children }) => {
  const queryKey = 'getPaginatedUserFeedFollows';
  const { data, loading, fetchMore } = useGetFollowedQuery({
    variables: { data: variables },
  });

  const hasData = data?.[queryKey];

  useEffect(() => {
    if (!hasData) {
      return;
    }

    const { title_lower, client_id__feed__id } = data[queryKey];
    const canPaginate = !!title_lower;

    if (!canPaginate) {
      return;
    }

    fetchMore({
      updateQuery,
      variables: {
        data: {
          ...variables,
          title_lower,
          client_id__feed__id,
        },
      },
    });
  }, [hasData]);

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, options) => {
    const { fetchMoreResult } = options;

    if (!fetchMoreResult) {
      return prev;
    }

    return {
      [queryKey]: {
        ...fetchMoreResult[queryKey],
        feed_follows: [...prev[queryKey].feed_follows, ...fetchMoreResult[queryKey].feed_follows],
      },
    };
  };

  if (loading || !data?.[queryKey]) {
    return (
      <Fragment>
        {children({
          count: 0,
          followed: [],
        })}
      </Fragment>
    );
  }

  const { feed_follows, count } = data[queryKey];

  return (
    <Fragment>
      {children({
        count,
        followed: feed_follows as UserFeedFollow[],
      })}
    </Fragment>
  );
};

export default GetFollowed;
